import React, { useEffect } from 'react';
import { 
  CheckCircle,
  XCircle,
  Cpu,
  Clock,
  HardDrive,
  Server,
  Globe
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';

const StatusIndicator = ({ active }) => (
  <div className="flex items-center mb-4">
    {active ? (
      <CheckCircle className="text-green-600 mr-2 w-5 h-5" />
    ) : (
      <XCircle className="text-red-600 mr-2 w-5 h-5" />
    )}
    <span className="font-medium">
      {active ? 'Active' : 'Inactive'}
    </span>
  </div>
);

const MetricCard = ({ icon: Icon, title, value, color = 'text-blue-600' }) => (
  <Card className="h-full rounded-lg border border-gray-200">
    <CardContent className="p-4">
      <div className="flex items-center mb-2">
        <Icon className={`mr-2 w-5 h-5 ${color}`} />
        <span className="text-sm text-gray-600">{title}</span>
      </div>
      <div className="text-2xl font-medium">{value}</div>
    </CardContent>
  </Card>
);

const ResourceUsageCard = ({ title, value, icon: Icon, color }) => (
  <Card className="h-full rounded-lg border border-gray-200">
    <CardContent className="p-4">
      <div className="flex justify-between items-center mb-2">
        <span className="text-sm text-gray-600">{title}</span>
        <Icon className={`w-4 h-4 ${color}`} />
      </div>
      
      <div className="flex items-center mb-2">
        <span className="text-lg font-medium mr-2">{value}%</span>
        <Progress 
          value={value} 
          className={`flex-1 h-2 ${
            value > 80 ? 'bg-red-100' : value > 60 ? 'bg-yellow-100' : 'bg-blue-100'
          }`}
        />
      </div>
    </CardContent>
  </Card>
);

export default function ClusterDashboard({ 
  clusters, 
  user, 
  fetchClusters, 
  fetchClusterHealth, 
  fetchServiceStatus, 
  fetchApps 
}) {
  // Extract stable user properties to prevent infinite loops
  const userId = user?.user?._id;
  const userToken = user?.token;

  useEffect(() => {
    if (userId && userToken) {
      fetchClusters(userId, userToken);
      fetchClusterHealth(userId, userToken);
      fetchServiceStatus(userId, userToken);
      fetchApps(userId, userToken);
    }
  }, [userId, userToken, fetchClusters, fetchClusterHealth, fetchServiceStatus, fetchApps]);

  // Calculate aggregate metrics from all clusters
  const aggregateMetrics = React.useMemo(() => {
    if (!clusters || clusters.length === 0) {
      return {
        active: false,
        nodeCount: 0,
        uptime: 'N/A',
        version: 'N/A',
        endpoint: 'N/A'
      };
    }
    
    // Find the production cluster or the first running cluster
    const primaryCluster = clusters.find(c => c.name === 'Production' && c.status === 'running') || 
                          clusters.find(c => c.status === 'running') || 
                          clusters[0];
                          
    // Count all nodes across clusters
    const totalNodes = clusters.reduce((sum, cluster) => sum + cluster.nodes, 0);
    
    return {
      active: primaryCluster.status === 'running',
      nodeCount: totalNodes,
      uptime: primaryCluster.uptime || '15d 7h 23m', // Default if not provided in mock
      version: primaryCluster.version,
      endpoint: primaryCluster.endpoint || '192.168.1.100:6443' // Default if not provided in mock
    };
  }, [clusters]);
  
  // Mock resource usage data - this would come from API in a real app
  // In production, you'd fetch this data from backend through Redux
  const resourceUsage = {
    cpu: 42,
    memory: 38,
    storage: 26,
    pods: 63,
    networkIn: '24.6 MB/s',
    networkOut: '12.3 MB/s'
  };

  return (
    <div className="px-6">
      <h2 className="text-lg font-medium mb-4">
        Cluster Dashboard
      </h2>
      
      {/* Overview Section */}
      <Card className="p-6 mb-6 rounded-xl border border-gray-200">
        <h3 className="text-base font-bold mb-4">
          Cluster Overview
        </h3>
        
        <StatusIndicator active={aggregateMetrics.active} />
        
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
          <MetricCard 
            icon={Server} 
            title="Nodes" 
            value={aggregateMetrics.nodeCount} 
          />
          <MetricCard 
            icon={Clock} 
            title="Uptime" 
            value={aggregateMetrics.uptime} 
            color="text-green-600" 
          />
          <MetricCard 
            icon={Globe} 
            title="Version" 
            value={aggregateMetrics.version} 
            color="text-cyan-600" 
          />
          <MetricCard 
            icon={HardDrive} 
            title="Control Plane" 
            value={aggregateMetrics.endpoint} 
            color="text-purple-600" 
          />
        </div>
      </Card>
      
      {/* Resource Usage Section */}
      <Card className="p-6 rounded-xl border border-gray-200">
        <h3 className="text-base font-bold mb-4">
          Resource Utilization
        </h3>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
          <ResourceUsageCard 
            title="CPU Usage" 
            value={resourceUsage.cpu}
            icon={Server}
            color="text-blue-600"
          />
          <ResourceUsageCard 
            title="Memory Usage" 
            value={resourceUsage.memory}
            icon={Cpu}
            color="text-purple-600"
          />
          <ResourceUsageCard 
            title="Storage Usage" 
            value={resourceUsage.storage}
            icon={HardDrive}
            color="text-cyan-600"
          />
          <ResourceUsageCard 
            title="Pod Allocation" 
            value={resourceUsage.pods}
            icon={Server}
            color="text-orange-600"
          />
        </div>
        
        {/* Network Stats */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <h4 className="text-sm font-medium mb-3">Network Traffic</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
              <span className="text-sm text-gray-600">Inbound</span>
              <span className="font-medium">{resourceUsage.networkIn}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
              <span className="text-sm text-gray-600">Outbound</span>
              <span className="font-medium">{resourceUsage.networkOut}</span>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
} 